import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AppBar, Toolbar, Typography, Container, CssBaseline, Box, Card, Button, CircularProgress, Menu, MenuItem, IconButton, useMediaQuery, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { 
  Work as WorkIcon, 
  Search as ResearchIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  Home as HomeIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import BillUpload from './components/BillUpload';
import ComparisonDashboard from './components/ComparisonDashboard';
import Research from './components/Research';
import Settings from './components/Settings';
import DetailedLoadingProgress from './components/DetailedLoadingProgress';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VERSION_INFO } from './constants/version';
import './App.css';

interface AnalysisResult {
  oci_mappings: any[];
  claude_analysis: any;
  cost_comparison: any;
  bom: any[];
  differentiators: any[];
  bill_name?: string;
}

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      dark: '#115293',
      light: '#42a5f5',
    },
    secondary: {
      main: '#4c2a85',
      dark: '#3a1f64',
      light: '#764ba2',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    grey: {
      50: '#f8f9fa',
      100: '#f3f4f6',
      200: '#e9ecef',
      300: '#dee2e6',
      500: '#6c757d',
      600: '#495057',
      900: '#111827',
    },
    success: {
      main: '#155724',
      light: '#e8f5e8',
    },
    warning: {
      main: '#0c5460',
      light: '#e7f3ff',
    },
    error: {
      main: '#dc3545',
      light: '#f8d7da',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#6c757d'
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h4: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #e5e7eb',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#111827',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          borderBottom: '1px solid #e5e7eb',
        },
      },
    },
  },
});

function AuthenticatedApp() {
  const { logout } = useAuth();
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'analysis' | 'research' | 'settings'>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleAnalysisComplete = (result: AnalysisResult) => {
    setAnalysisResult(result);
    setLoading(false);
    setCurrentPage('analysis');
  };

  const handleAnalysisStart = () => {
    setLoading(true);
  };

  const resetAnalysis = () => {
    setAnalysisResult(null);
    setLoading(false);
  };

  const handleGoHome = () => {
    setCurrentPage('home');
  };

  const handleNewAnalysis = () => {
    setAnalysisResult(null);
    setLoading(false);
    setCurrentPage('home');
  };

  const navigateToPage = (page: 'home' | 'analysis' | 'research' | 'settings') => {
    setCurrentPage(page);
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileNavigation = (page: 'home' | 'analysis' | 'research' | 'settings') => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <AppBar 
          position="static" 
          elevation={1}
          sx={{
            background: 'var(--white)',
            color: 'var(--dark-text)',
            boxShadow: 'var(--shadow-subtle)',
            height: '60px',
            justifyContent: 'center'
          }}
        >
          <Toolbar>
            {/* Brand/Logo */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer'
              }}
              onClick={handleGoHome}
            >
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 'var(--border-radius-small)',
                background: 'var(--primary-gradient)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 'var(--spacing-lg)'
              }}>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  AC
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  mr: 4,
                  fontSize: 'var(--app-title-size)',
                  fontWeight: 700,
                  color: 'var(--dark-text)',
                  fontFamily: 'var(--font-family)',
                  display: { xs: 'none', md: 'block' }
                }}
              >
                Alfred Cloud Compare
              </Typography>
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, mr: analysisResult ? 2 : 6 }}>
              <Button 
                color="inherit"
                startIcon={<HomeIcon />}
                onClick={() => navigateToPage('home')}
                sx={{ 
                  px: 'var(--spacing-lg)',
                  color: currentPage === 'home' ? 'var(--primary-blue)' : 'var(--medium-gray)',
                  backgroundColor: currentPage === 'home' ? 'var(--warning-bg)' : 'transparent',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--sidebar-size)',
                  fontWeight: currentPage === 'home' ? 600 : 400,
                  borderRadius: 'var(--border-radius-small)',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: currentPage === 'home' ? 'var(--warning-bg)' : 'var(--light-background)',
                    color: currentPage === 'home' ? 'var(--primary-blue)' : 'var(--dark-gray)'
                  }
                }}
              >
                Home
              </Button>

              <Button 
                color="inherit"
                startIcon={<ResearchIcon />}
                onClick={() => navigateToPage('research')}
                sx={{ 
                  px: 'var(--spacing-lg)',
                  color: currentPage === 'research' ? 'var(--primary-blue)' : 'var(--medium-gray)',
                  backgroundColor: currentPage === 'research' ? 'var(--warning-bg)' : 'transparent',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--sidebar-size)',
                  fontWeight: currentPage === 'research' ? 600 : 400,
                  borderRadius: 'var(--border-radius-small)',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: currentPage === 'research' ? 'var(--warning-bg)' : 'var(--light-background)',
                    color: currentPage === 'research' ? 'var(--primary-blue)' : 'var(--dark-gray)'
                  }
                }}
              >
                Research
              </Button>

              <Button 
                color="inherit"
                startIcon={<SettingsIcon />}
                onClick={() => navigateToPage('settings')}
                sx={{ 
                  px: 'var(--spacing-lg)',
                  color: currentPage === 'settings' ? 'var(--primary-blue)' : 'var(--medium-gray)',
                  backgroundColor: currentPage === 'settings' ? 'var(--warning-bg)' : 'transparent',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--sidebar-size)',
                  fontWeight: currentPage === 'settings' ? 600 : 400,
                  borderRadius: 'var(--border-radius-small)',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: currentPage === 'settings' ? 'var(--warning-bg)' : 'var(--light-background)',
                    color: currentPage === 'settings' ? 'var(--primary-blue)' : 'var(--dark-gray)'
                  }
                }}
              >
                Settings
              </Button>

              <Button 
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ 
                  px: 'var(--spacing-lg)',
                  color: 'var(--medium-gray)',
                  fontFamily: 'var(--font-family)',
                  fontSize: 'var(--sidebar-size)',
                  fontWeight: 400,
                  borderRadius: 'var(--border-radius-small)',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: 'var(--light-background)',
                    color: 'var(--dark-gray)'
                  }
                }}
              >
                Logout
              </Button>
            </Box>

            {/* Mobile Navigation */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
              <IconButton
                color="inherit"
                aria-label="open menu"
                onClick={handleMobileMenuToggle}
                sx={{ 
                  color: 'var(--dark-text)',
                  p: 1.5
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Bill Status & Actions - Desktop Only */}
            {analysisResult && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={handleNewAnalysis}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: 'primary.main', 
                    px: 2, 
                    py: 0.75, 
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'primary.dark',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      borderColor: 'primary.light',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'white', mr: 1, fontWeight: 500 }}>
                    Active Bill:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                    {analysisResult?.bill_name || 'AWS Analysis'}
                  </Typography>
                  <EditIcon sx={{ color: 'rgba(255,255,255,0.8)', ml: 1, fontSize: 16 }} />
                </Button>
              </Box>
            )}
          </Toolbar>
        </AppBar>
        
        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 280,
              bgcolor: 'background.paper',
              pt: 2
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Alfred Cloud Compare
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          <List sx={{ pt: 1 }}>
            <ListItem 
              component="div"
              onClick={() => handleMobileNavigation('home')}
              sx={{ 
                bgcolor: currentPage === 'home' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: 'grey.100' },
                py: 1.5,
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ color: currentPage === 'home' ? 'primary.main' : 'text.secondary' }}>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Home" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: currentPage === 'home' ? 600 : 400,
                    color: currentPage === 'home' ? 'primary.main' : 'text.primary'
                  }
                }}
              />
            </ListItem>
            
            <ListItem 
              component="div"
              onClick={() => handleMobileNavigation('research')}
              sx={{ 
                bgcolor: currentPage === 'research' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: 'grey.100' },
                py: 1.5,
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ color: currentPage === 'research' ? 'primary.main' : 'text.secondary' }}>
                <ResearchIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Research" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: currentPage === 'research' ? 600 : 400,
                    color: currentPage === 'research' ? 'primary.main' : 'text.primary'
                  }
                }}
              />
            </ListItem>
            
            <ListItem 
              component="div"
              onClick={() => handleMobileNavigation('settings')}
              sx={{ 
                bgcolor: currentPage === 'settings' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: 'grey.100' },
                py: 1.5,
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ color: currentPage === 'settings' ? 'primary.main' : 'text.secondary' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Settings" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: currentPage === 'settings' ? 600 : 400,
                    color: currentPage === 'settings' ? 'primary.main' : 'text.primary'
                  }
                }}
              />
            </ListItem>
            
            {analysisResult && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    <EditIcon />
                  </ListItemIcon>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      Active Bill:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {analysisResult?.bill_name || 'AWS Analysis'}
                    </Typography>
                  </Box>
                </ListItem>
                <ListItem 
                  component="div"
                  onClick={() => { handleNewAnalysis(); setMobileMenuOpen(false); }}
                  sx={{ 
                    '&:hover': { bgcolor: 'grey.100' },
                    py: 1.5,
                    cursor: 'pointer'
                  }}
                >
                  <ListItemText 
                    primary="New Analysis" 
                    sx={{ 
                      textAlign: 'center',
                      '& .MuiListItemText-primary': {
                        color: 'primary.main',
                        fontWeight: 500
                      }
                    }}
                  />
                </ListItem>
              </>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            <ListItem 
              component="div"
              onClick={handleLogout}
              sx={{ 
                '&:hover': { bgcolor: 'grey.100' },
                py: 1.5,
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ color: 'text.secondary' }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Logout" 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    color: 'text.primary'
                  }
                }}
              />
            </ListItem>
          </List>
        </Drawer>
        
        <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pt: 1 }}>
          <Container maxWidth="lg" sx={{ pb: 2 }}>
            
            {/* Home Page - Bill Upload */}
            {currentPage === 'home' && !loading && (
              <BillUpload 
                onAnalysisStart={handleAnalysisStart}
                onAnalysisComplete={handleAnalysisComplete}
              />
            )}
            
            {/* Loading State */}
            {loading && (
              <DetailedLoadingProgress />
            )}
            
            {/* Analysis Results */}
            {currentPage === 'analysis' && analysisResult && !loading && (
              <ComparisonDashboard 
                analysisResult={analysisResult}
                onReset={resetAnalysis}
              />
            )}
            
            {/* Research Page */}
            {currentPage === 'research' && (
              <Research />
            )}
            
            {/* Settings Page */}
            {currentPage === 'settings' && (
              <Settings analysisResult={analysisResult} />
            )}
          </Container>
        </Box>
        
        {/* Version Footer */}
        <Box 
          sx={{ 
            position: 'fixed',
            bottom: 16,
            right: 16,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#9e9e9e', 
              fontSize: '0.7rem' 
            }}
          >
            {VERSION_INFO}
          </Typography>
        </Box>
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, loading, login } = useAuth();

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={login} />
      </ThemeProvider>
    );
  }

  return <AuthenticatedApp />;
}

export default App;